import type { DragEvent } from "react";
import { memo, useState } from "react";
import { styled } from "styled-components";

import { useLazyGetDimensionQuery } from "api/dimensionApi.slice";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { addDimensionToMapSuccess, removeTopicFromMap, toggleDraggingCustomMap } from "domains/createMapDetail/actions";
import { AddRemoveButton } from "domains/createMapDetail/components/keyIssueList/AddRemoveButton";
import { EditCustomKeyIssueButton } from "domains/createMapDetail/components/keyIssueList/EditCustomKeyIssueButton";
import { useTrackDimensions } from "domains/dimension/useTrackDimensions";
import { KeyIssueTile } from "domains/topic/components/KeyIssueTile";
import { getCreateMapCustomKeyIssueAccess } from "entities/permissions/selectors";

const StyledContainer = styled.div`
  cursor: ${({ draggable, onClick }) => {
    if (draggable) {
      return "move";
    }
    return onClick ? "pointer" : "initial";
  }};
  margin-bottom: 1rem;
`;

const StyledFlexContainer = styled.div`
  display: flex;
  height: 100%;
  & > button {
    width: 50%;
    padding: 0;
    text-align: center;
    justify-content: center;
  }
`;

type DimensionItemProps = {
  dataTestId?: string;
  description: string;
  id: string;
  image_url: string | null;
  is_custom_dimension: boolean;
  isOnMap: boolean;
  name: string;
  onEditSelect?: (id: string) => void;
  parent_topic_id: string | undefined;
  shouldHighlightSearchTerm?: boolean;
};

export const DimensionItem = memo(
  ({
    dataTestId,
    description,
    id,
    image_url,
    is_custom_dimension,
    isOnMap,
    name,
    onEditSelect,
    parent_topic_id,
    shouldHighlightSearchTerm = true,
  }: DimensionItemProps) => {
    const dispatch = useDispatch();
    const [fetchDimension, dimensionResult] = useLazyGetDimensionQuery();
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const searchTerm = useSelector((state) => state.dimension.search);
    const hasAccessToCustomKeyIssues = useSelector(getCreateMapCustomKeyIssueAccess);
    const { trackAddDimension, trackRemoveDimension } = useTrackDimensions();

    const handleDragStart = (event: DragEvent<HTMLDivElement>) => {
      event.dataTransfer.setData("topicId", id);

      dispatch(toggleDraggingCustomMap({ isDragging: true }));
    };

    const handleDragEnd = () => {
      dispatch(toggleDraggingCustomMap({ isDragging: false }));
    };

    const onAddKeyIssue = () =>
      void fetchDimension({ id }, true).then((result) => {
        if (!result.isSuccess) return;
        dispatch(addDimensionToMapSuccess(result.data.dimension.id));
        trackAddDimension(result.data.dimension.id);
      });

    const onRemoveKeyIssue = () => {
      dispatch(removeTopicFromMap({ keyIssueId: id }));
      trackRemoveDimension(id);
    };

    const toggleDescription = () => {
      if (!description) return;

      setIsExpanded((prev) => !prev);
    };

    const highlightedTerm = shouldHighlightSearchTerm ? searchTerm : undefined;

    return (
      <StyledContainer
        draggable={!isOnMap}
        onClick={toggleDescription}
        onDragStart={!isOnMap ? handleDragStart : undefined}
        onDragEnd={!isOnMap ? handleDragEnd : undefined}
      >
        <KeyIssueTile
          actionElements={
            is_custom_dimension && hasAccessToCustomKeyIssues ? (
              <StyledFlexContainer>
                <EditCustomKeyIssueButton id={id} onEditSelect={onEditSelect} />
                <AddRemoveButton
                  isPending={dimensionResult.isFetching}
                  isAdded={isOnMap}
                  onAdd={onAddKeyIssue}
                  onRemove={onRemoveKeyIssue}
                  dataTestId={dataTestId}
                />
              </StyledFlexContainer>
            ) : (
              <AddRemoveButton
                isPending={dimensionResult.isFetching}
                isAdded={isOnMap}
                onAdd={onAddKeyIssue}
                onRemove={onRemoveKeyIssue}
                dataTestId={dataTestId}
              />
            )
          }
          actionElementsPosition="below"
          description={description}
          highlightedTerm={highlightedTerm}
          image_url={image_url ?? undefined}
          isDescriptionExpanded={isExpanded}
          onToggleDescription={toggleDescription}
          name={name}
          parent_topic_id={parent_topic_id}
        />
      </StyledContainer>
    );
  },
);

DimensionItem.displayName = "DimensionItem";
